import api from "../../../utils/api";
import { PAYROLL_ENDPOINTS } from "@torre/payroll";
import type {
  AttendanceRecord,
  AttendanceSummary,
  PayrollAdjustment,
  WorkLog,
  ContributionParam,
  PayrollPeriod,
  Payslip,
  WorkLogStatus,
  PageMeta,
  CalculationPreviewResponse,
  ReceiptResponse,
  StampedResponse,
  PayrollPayment,
  PayrollPaymentInput,
  PaymentMethod,
  PaidPeriodsResponse,
  ExtraordinaryMovementsResponse,
  ExtraordinarySource,
  ExtraordinaryClassification,
  SalaryChange,
  SalaryChangeInput,
  SalaryDetection,
} from "../types";

// ---------------------------------------------------------------------------
// Cliente API de la mini-app Nómina. Habla con el módulo `payslip` de
// torre-de-control-api (rutas declaradas en @torre/payroll, así que los paths no
// se duplican entre capas).
//
// Dos envelopes de respuesta:
//   • pageResponse → { meta, data: [...] }             (listas)
//   • response     → { meta, data: { message, data } } (single / acciones)
// `unwrap` colapsa ambos al payload interno.
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap(res: any) {
  const d = res?.data?.data;
  if (d && typeof d === "object" && !Array.isArray(d) && "data" in d) {
    return d.data;
  }
  return d;
}

export interface ListParams {
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

// --- Asistencia (AgroCloud#36) -------------------------------------------------------
export async function fetchAttendances(
  params: ListParams = {},
): Promise<{ items: AttendanceRecord[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollAttendanceList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

/** Filtros del listado de asistencia (#1143). Fechas ISO YYYY-MM-DD, inclusivas. */
export interface AttendanceFilterParams {
  from?: string;
  to?: string;
  /** Una persona; lo ignora el alcance del servidor si no es la tuya y no administras. */
  teamMemberId?: string;
}

/** Tope de páginas al traer un rango completo: evita un bucle si el meta viene mal. */
const ATTENDANCE_MAX_PAGES = 40;
const ATTENDANCE_PAGE_SIZE = 500;

/**
 * Todas las jornadas del rango (#1143), página por página: la tabla y el
 * resumen tienen que mostrar el rango entero, no las primeras 100 filas.
 * El servidor filtra por `from`/`to`/`teamMemberId` y acota a lo propio.
 */
export async function fetchAllAttendances(
  filters: AttendanceFilterParams = {},
): Promise<{ items: AttendanceRecord[]; meta: PageMeta }> {
  const params: ListParams = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.teamMemberId) params.teamMemberId = filters.teamMemberId;

  const items: AttendanceRecord[] = [];
  let meta: PageMeta = {};
  for (let page = 1; page <= ATTENDANCE_MAX_PAGES; page += 1) {
    const res = await fetchAttendances({ ...params, page, limit: ATTENDANCE_PAGE_SIZE });
    items.push(...res.items);
    meta = res.meta;
    const totalPages = Number(res.meta?.totalPages) || 1;
    if (res.items.length < ATTENDANCE_PAGE_SIZE || page >= totalPages) break;
  }
  return { items, meta: { ...meta, totalItems: meta.totalItems ?? items.length } };
}

export async function createAttendance(
  payload: Partial<AttendanceRecord>,
): Promise<AttendanceRecord> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollAttendance, payload);
  return unwrap(res);
}

export async function updateAttendance(
  id: string,
  payload: Partial<AttendanceRecord>,
): Promise<AttendanceRecord> {
  const res = await api.put(PAYROLL_ENDPOINTS.payrollAttendance, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteAttendance(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payrollAttendance, { params: { id } });
}

export async function fetchAttendanceSummary(
  params: ListParams = {},
): Promise<AttendanceSummary[]> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollAttendanceSummary, { params });
  return unwrap(res) ?? [];
}

// --- Anticipos y otros haberes/descuentos (AgroCloud#37) -----------------------------
export async function fetchAdjustments(
  params: ListParams = {},
): Promise<{ items: PayrollAdjustment[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollAdjustmentList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createAdjustment(
  payload: Partial<PayrollAdjustment>,
): Promise<PayrollAdjustment> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollAdjustment, payload);
  return unwrap(res);
}

export async function updateAdjustment(
  id: string,
  payload: Partial<PayrollAdjustment>,
): Promise<PayrollAdjustment> {
  const res = await api.put(PAYROLL_ENDPOINTS.payrollAdjustment, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteAdjustment(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payrollAdjustment, { params: { id } });
}

// --- Incidencias del equipo y su autorización (AgroCloud#81) ---------------------------
export async function fetchWorkLogs(
  params: ListParams = {},
): Promise<{ items: WorkLog[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollWorkLogList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createWorkLog(
  payload: Partial<WorkLog>,
): Promise<WorkLog> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollWorkLog, payload);
  return unwrap(res);
}

export async function updateWorkLog(
  id: string,
  payload: Partial<WorkLog>,
): Promise<WorkLog> {
  const res = await api.put(PAYROLL_ENDPOINTS.payrollWorkLog, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteWorkLog(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payrollWorkLog, { params: { id } });
}

/**
 * Autoriza / rechaza / envía a nómina una incidencia (AgroCloud#81).
 *
 * La máquina de estados vive en el backend: aunque alguien llame esta función
 * con una transición ilegal, el back la rechaza. El front no es la barrera.
 */
export async function transitionWorkLog(
  id: string,
  status: WorkLogStatus,
  extra: { rejectionReason?: string; periodId?: string } = {},
): Promise<WorkLog> {
  const res = await api.post(
    PAYROLL_ENDPOINTS.payrollWorkLogTransition,
    { status, ...extra },
    { params: { id } },
  );
  return unwrap(res);
}

// --- Parámetros previsionales (AgroCloud#38) -----------------------------------------
export async function fetchContributions(
  params: ListParams = {},
): Promise<{ items: ContributionParam[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollContributionList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createContribution(
  payload: Partial<ContributionParam>,
): Promise<ContributionParam> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollContribution, payload);
  return unwrap(res);
}

export async function updateContribution(
  id: string,
  payload: Partial<ContributionParam>,
): Promise<ContributionParam> {
  const res = await api.put(PAYROLL_ENDPOINTS.payrollContribution, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteContribution(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payrollContribution, { params: { id } });
}

// --- Periodos de nómina (AgroCloud#82) -----------------------------------------------
export async function fetchPeriods(
  params: ListParams = {},
): Promise<{ items: PayrollPeriod[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payrollPeriodList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createPeriod(payload: Partial<PayrollPeriod>): Promise<PayrollPeriod> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollPeriod, payload);
  return unwrap(res);
}

export async function updatePeriod(
  id: string,
  payload: Partial<PayrollPeriod>,
): Promise<PayrollPeriod> {
  const res = await api.put(PAYROLL_ENDPOINTS.payrollPeriod, payload, { params: { id } });
  return unwrap(res);
}

export async function deletePeriod(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payrollPeriod, { params: { id } });
}

/** Cierra un periodo (AgroCloud#82). El back bloquea si hay liquidaciones incompletas. */
export async function closePeriod(id: string): Promise<PayrollPeriod> {
  const res = await api.post(PAYROLL_ENDPOINTS.payrollPeriodClose, {}, { params: { id } });
  return unwrap(res);
}

// --- Liquidaciones de sueldo (AgroCloud#38) ------------------------------------------
export async function fetchPayslips(
  params: ListParams = {},
): Promise<{ items: Payslip[]; meta: PageMeta }> {
  const res = await api.get(PAYROLL_ENDPOINTS.payslipsList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function fetchPayslip(id: string): Promise<Payslip> {
  const res = await api.get(PAYROLL_ENDPOINTS.payslip, { params: { id } });
  return unwrap(res);
}

export async function createPayslip(
  payload: Partial<Payslip> & { periodId?: string | null },
): Promise<Payslip> {
  const res = await api.post(PAYROLL_ENDPOINTS.payslip, payload);
  return unwrap(res);
}

export async function updatePayslip(
  id: string,
  payload: Record<string, unknown>,
): Promise<Payslip> {
  const res = await api.put(PAYROLL_ENDPOINTS.payslip, payload, { params: { id } });
  return unwrap(res);
}

export async function deletePayslip(id: string): Promise<void> {
  await api.delete(PAYROLL_ENDPOINTS.payslip, { params: { id } });
}

/**
 * Vista previa del cálculo SIN persistir (AgroCloud#38).
 *
 * El cálculo vive en el backend porque la fórmula del pago de la tarea es la
 * del módulo Equipo (`computeSettlementPayment`, AgroCloud#13 / PR AgroCloud#100) y las
 * tarifas y la asistencia se leen de Firestore: así el formulario muestra
 * exactamente lo que se va a guardar, con una sola fórmula en el monorepo.
 */
export async function previewPayslip(payload: {
  teamMemberId: string;
  periodId?: string | null;
  dailyRate?: number | null;
}): Promise<CalculationPreviewResponse> {
  const res = await api.post(PAYROLL_ENDPOINTS.payslipCalc, payload);
  return unwrap(res);
}

/** Aprueba una liquidación (AgroCloud#38). El back bloquea si está incompleta. */
export async function approvePayslip(id: string): Promise<Payslip> {
  const res = await api.post(PAYROLL_ENDPOINTS.payslipApprove, {}, { params: { id } });
  return unwrap(res);
}

// --- Cumplimiento fiscal CFDI (AgroCloud#82) -----------------------------------------

/** Arma y valida el comprobante SIN timbrar, para revisarlo antes (AgroCloud#82). */
export async function fetchReceipt(
  id: string,
  payload: { issuer?: Record<string, unknown>; recipient?: Record<string, unknown> } = {},
): Promise<ReceiptResponse> {
  const res = await api.post(PAYROLL_ENDPOINTS.payslipReceipt, payload, { params: { id } });
  return unwrap(res);
}

/**
 * Timbra la liquidación ante el SAT (AgroCloud#82).
 *
 * ⚠️ HOY SIEMPRE devuelve `status: "no_pac_configured"`: no hay PAC
 * (Proveedor Autorizado de Certificación) contratado en este despliegue. El
 * backend NO simula un timbrado — devolver un folio fiscal inventado sería
 * emitir un CFDI falso. La UI muestra el estado tal cual, en ámbar.
 *
 * TODO(AgroCloud#82): cuando se contrate un PAC y se implemente
 * `functions/src/services/pacService.js`, este mismo endpoint empieza a
 * devolver `status: "stamped"` con su UUID, sin tocar el front.
 */
export async function stampPayslip(
  id: string,
  payload: {
    issuer?: Record<string, unknown>;
    recipient?: Record<string, unknown>;
  } = {},
): Promise<StampedResponse> {
  const res = await api.post(PAYROLL_ENDPOINTS.payslipStamp, payload, { params: { id } });
  return unwrap(res);
}

// --- Pagos de nómina y su comprobante (#1025) --------------------------------------
// La instancia de axios fija `Content-Type: application/json`; sin esto el
// FormData se serializaría como JSON y el archivo nunca llegaría.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

/** Mensaje legible de un error de la API (o `null`). */
export function apiErrorMessage(err: unknown): string | null {
  const r = (err as { response?: { data?: { meta?: { message?: string }; message?: string } } })
    ?.response;
  return r?.data?.meta?.message || r?.data?.message || null;
}

export async function fetchPayments(
  params: { teamMemberId?: string; year?: string } = {},
): Promise<{ items: PayrollPayment[]; isStaffAdmin: boolean }> {
  const res = await api.get("/payrollPaymentList", { params });
  const d = res?.data?.data ?? {};
  return { items: Array.isArray(d.data) ? d.data : [], isStaffAdmin: d.isStaffAdmin === true };
}

export async function createPayment(payload: PayrollPaymentInput): Promise<PayrollPayment> {
  return unwrap(await api.post("/payrollPayment", payload));
}

export async function updatePayment(
  id: string,
  payload: Partial<PayrollPaymentInput>,
): Promise<PayrollPayment> {
  return unwrap(await api.put("/payrollPayment", payload, { params: { id } }));
}

export async function deletePayment(id: string): Promise<void> {
  await api.delete("/payrollPayment", { params: { id } });
}

/** Registra el sueldo del padrón de un periodo para todo el equipo de sueldo fijo. */
export async function runPayments(payload: {
  period: string;
  paidAt: string;
  method: PaymentMethod;
  reference?: string;
}): Promise<{ created: PayrollPayment[]; skipped: { teamMemberId: string; reason: string }[] }> {
  return unwrap(await api.post("/payrollPaymentRun", payload));
}

export async function uploadPaymentProof(id: string, file: File): Promise<PayrollPayment> {
  const form = new FormData();
  form.append("file", file);
  return unwrap(await api.post("/payrollPaymentProof", form, { ...MULTIPART, params: { id } }));
}

export async function removePaymentProof(id: string): Promise<PayrollPayment> {
  return unwrap(await api.delete("/payrollPaymentProof", { params: { id } }));
}

/**
 * Registro de periodos pagados (#1141): agrupado por quincena o mes, dentro de
 * un rango de meses y, si se pide, solo de ciertos colaboradores o squads.
 */
export async function fetchPaidPeriods(params: {
  groupBy: "half" | "month";
  from?: string;
  to?: string;
  teamMemberIds?: string[];
  squadIds?: string[];
}): Promise<PaidPeriodsResponse> {
  const res = await api.get("/payrollPaidPeriods", {
    params: {
      groupBy: params.groupBy,
      from: params.from || undefined,
      to: params.to || undefined,
      teamMemberIds: params.teamMemberIds?.length ? params.teamMemberIds.join(",") : undefined,
      squadIds: params.squadIds?.length ? params.squadIds.join(",") : undefined,
    },
  });
  return unwrap(res);
}

/** URL firmada (15 min) del comprobante bancario del pago. */
export async function fetchPaymentProofUrl(id: string): Promise<{ url: string; name: string }> {
  return unwrap(await api.get("/payrollPaymentProofUrl", { params: { id } }));
}

/** Recibo de pago en PDF generado por el servidor. */
export async function fetchPaymentReceipt(id: string): Promise<Blob> {
  const res = await api.get("/payrollPaymentReceipt", { params: { id }, responseType: "blob" });
  return res.data as Blob;
}

// --- Movimientos extraordinarios (#1376) ---------------------------------------------
/** Pagos y gastos de nómina que no son un pago normal de quincena. */
export async function fetchExtraordinaryMovements(
  params: { teamMemberId?: string; from?: string; to?: string; includeRegular?: boolean } = {},
): Promise<ExtraordinaryMovementsResponse> {
  const res = await api.get("/payrollExtraordinaryMovements", {
    params: {
      teamMemberId: params.teamMemberId || undefined,
      from: params.from || undefined,
      to: params.to || undefined,
      includeRegular: params.includeRegular ? "true" : undefined,
    },
  });
  return unwrap(res);
}

/** Clasifica un movimiento detectado; `null` quita la clasificación. */
export async function reviewExtraordinaryMovement(payload: {
  source: ExtraordinarySource;
  sourceId: string;
  classification: ExtraordinaryClassification | null;
  note?: string | null;
  /** #1491: solo con `reimbursement`: negocio o lead del CRM al que se carga. */
  dealId?: string | null;
  leadId?: string | null;
}): Promise<void> {
  await api.put("/payrollExtraordinaryReview", payload);
}

// --- Historial de sueldos (#1487) -------------------------------------------
/** Bitácora de una persona (o de todas, para quien administra), en orden cronológico. */
export async function fetchSalaryHistory(teamMemberId?: string): Promise<{ items: SalaryChange[]; isStaffAdmin: boolean }> {
  const res = await api.get("/payrollSalaryHistory", { params: { teamMemberId: teamMemberId || undefined } });
  return unwrap(res);
}

/** Registra un cambio de sueldo a mano. Devuelve las entradas creadas (puede abrir el «desde siempre»). */
export async function createSalaryChange(payload: SalaryChangeInput): Promise<{ items: SalaryChange[]; rosterUpdated: boolean }> {
  const res = await api.post("/payrollSalaryChange", payload);
  return unwrap(res);
}

export async function updateSalaryChange(id: string, payload: Partial<Omit<SalaryChangeInput, "teamMemberId" | "applyToRoster">>): Promise<SalaryChange> {
  const res = await api.put("/payrollSalaryChange", payload, { params: { id } });
  return unwrap(res);
}

export async function deleteSalaryChange(id: string): Promise<void> {
  await api.delete("/payrollSalaryChange", { params: { id } });
}

/**
 * Aumentos detectados por los pagos (una persona o todas). Con `apply` escribe
 * las propuestas en la bitácora de quien aún no la tiene.
 */
export async function detectSalaryChanges(params: { teamMemberId?: string; apply?: boolean } = {}): Promise<{ items: SalaryDetection[]; applied: number }> {
  const res = await api.post("/payrollSalaryHistoryDetect", { teamMemberId: params.teamMemberId || undefined, apply: params.apply === true });
  return unwrap(res);
}
